import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "./Logo";
import { NAV, WA_ORCAMENTO, scrollToId } from "../lib/site";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (window.__lenis) {
      open ? window.__lenis.stop() : window.__lenis.start();
    }
  }, [open]);

  const goTo = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 250 : 0);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled
            ? "border-b border-white/10 bg-[#030305]/70 backdrop-blur-xl"
            : "border-b border-transparent bg-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 lg:px-10">
          <Logo />
          <nav className="hidden items-center gap-8 lg:flex" aria-label="Navegação principal">
            {NAV.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => goTo(item.id)}
                data-testid={`nav-link-${item.id}`}
                className="group relative text-sm font-medium text-white/65 transition-colors duration-300 hover:text-white"
              >
                {item.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] transition-[width] duration-300 group-hover:w-full" />
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <a
              href={WA_ORCAMENTO}
              target="_blank"
              rel="noreferrer"
              data-testid="header-cta-button"
              className="hidden items-center gap-2 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_10px_36px_-12px_rgba(0,229,255,0.55)] transition-[transform,filter] duration-300 hover:scale-[1.03] hover:brightness-110 lg:inline-flex"
            >
              Solicitar orçamento
              <ArrowUpRight className="h-4 w-4" />
            </a>
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-button"
              aria-label="Abrir menu"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white backdrop-blur-md transition-colors duration-300 hover:bg-white/10 lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[60] flex flex-col bg-[#030305]/95 backdrop-blur-2xl lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex h-[72px] items-center justify-between px-6">
              <Logo />
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-close-button"
                aria-label="Fechar menu"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8" aria-label="Menu móvel">
              {NAV.map((item, i) => (
                <motion.button
                  key={item.id}
                  type="button"
                  initial={{ opacity: 0, x: -28 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.07, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  onClick={() => goTo(item.id)}
                  data-testid={`mobile-nav-link-${item.id}`}
                  className="border-b border-white/10 py-5 text-left font-display text-3xl font-bold tracking-tight text-white/85 transition-colors duration-300 hover:text-white"
                >
                  {item.label}
                </motion.button>
              ))}
              <motion.a
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                href={WA_ORCAMENTO}
                target="_blank"
                rel="noreferrer"
                data-testid="mobile-cta-button"
                className="mt-10 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] px-7 py-4 text-base font-semibold text-white"
              >
                Solicitar orçamento
                <ArrowUpRight className="h-5 w-5" />
              </motion.a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
